class PromoPopup {
  constructor(container) {
    this.container = container;
    this.popupModal = container.querySelector(".sign-up-popup-modal");
    this.overlay = container.querySelector(".sign-up-popup-overlay");
    this.closeButton = this.popupModal.querySelector(".popup-modal__close");
    this.dismissButton = this.popupModal.querySelector(
      ".popup-modal__dismiss-btn"
    );

    this.form = this.popupModal.querySelector(".newsletter-form");
    this.emailInput = this.form?.querySelector('input[type="email"]');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.formError = this.form?.querySelector(".newsletter-form__message");

    this.successModal = container.querySelector(".success-popup-modal");
    this.successOverlay = container.querySelector(".success-popup-overlay");
    this.copyButton = this.successModal?.querySelector(".popup-modal__copy-btn");
    this.copySuccessMsg = this.successModal?.querySelector(".popup-modal__success-msg");
    this.successCloseButton = this.successModal?.querySelector(".popup-modal__close");
    this.successDismissButton = this.successModal?.querySelector(".popup-modal__dismiss-btn");

    this.tab = container.querySelector(".promo-popup__tab");

    this.sectionId = this.popupModal.dataset.sectionId;
    this.testMode = this.popupModal.dataset.testMode === "true";
    this.delaySeconds = parseInt(this.popupModal.dataset.delaySeconds);
    this.delayDays = parseInt(this.popupModal.dataset.delayDays);
    this.displayTimer = this.popupModal.dataset.displayTimer === "true";
    this.timerDuration = parseFloat(this.popupModal.dataset.timerDuration) * 60;

    this.minutesElement = this.popupModal.querySelector(".popup-modal__timer__minutes");
    this.secondsElement = this.popupModal.querySelector(".popup-modal__timer__seconds");

    this.storageKey = `promo-popup-${window.location.host}-${this.sectionId}`;
    this.subscribedKey = "promo-bar-subscribed-" + window.location.host;

    // Tracks the pending auto-open timer so it can be cancelled on subscribe
    this.autoShowTimer = null;

    this.init();
  }

  init() {
    this.setupEventListeners();

    // Wire the form regardless of subscribed state — the subscribed flag
    // only suppresses auto-triggers, not the ability to manually submit.
    if (this.form) {
      this.setupFormSubmission();
    }

    if (this.isSubscribed()) {
      console.log("[WavePlug popup] Already subscribed — scroll trigger suppressed. To retest, run in console: localStorage.removeItem('" + this.subscribedKey + "')");
      this.showTab();
      return;
    }

    this.checkAndShowPopup();

    if (this.testMode) {
      if (this.displayTimer) this.startTimer();
      return;
    }
  }

  isSubscribed() {
    try {
      const stored = localStorage.getItem(this.subscribedKey);
      return stored ? JSON.parse(stored).subscribed === true : false;
    } catch (_) {
      return false;
    }
  }

  setupEventListeners() {
    this.closeButton.addEventListener("click", () => this.closePopup());
    if (this.dismissButton) {
      this.dismissButton.addEventListener("click", () => this.closePopup());
    }
    this.overlay.addEventListener("click", () => this.closePopup());

    if (this.tab) {
      this.tab.addEventListener("click", () => this.showPopup());
    }

    if (this.successModal) {
      if (this.copyButton) {
        this.copyButton.addEventListener("click", () => this.handleCopyCode());
      }
      if (this.successCloseButton) {
        this.successCloseButton.addEventListener("click", () => this.closeSuccessPopup());
      }
      if (this.successDismissButton) {
        this.successDismissButton.addEventListener("click", () => this.closeSuccessPopup());
      }
      if (this.successOverlay) {
        this.successOverlay.addEventListener("click", () => this.closeSuccessPopup());
      }
    }

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (this.popupModal.classList.contains("popup-modal--active")) {
        this.closePopup();
      } else if (this.successModal?.classList.contains("popup-modal--active")) {
        this.closeSuccessPopup();
      }
    });
  }

  setupFormSubmission() {
    this.form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const email = this.emailInput?.value?.trim();
      if (!email) return;

      const apiKey = this.popupModal.dataset.klaviyoApiKey;
      const listId = this.popupModal.dataset.klaviyoListId;

      this.submitButton.disabled = true;

      try {
        if (apiKey && listId) {
          const response = await fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(apiKey)}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "revision": "2023-02-22",
              },
              body: JSON.stringify({
                data: {
                  type: "subscription",
                  attributes: {
                    profile: {
                      data: {
                        type: "profile",
                        attributes: { email },
                      },
                    },
                  },
                  relationships: {
                    list: {
                      data: { type: "list", id: listId },
                    },
                  },
                },
              }),
            }
          );

          if (response.status !== 202) {
            const body = await response.json().catch(() => ({}));
            const detail = body?.errors?.[0]?.detail;
            throw new Error(detail || `Klaviyo error (${response.status})`);
          }
        } else {
          console.warn(
            "[WavePlug] Klaviyo credentials not set — showing success popup for UI testing. " +
            "Add keys in Theme Editor → Promo Pop-Up → Klaviyo Integration."
          );
        }

        localStorage.setItem(this.subscribedKey, JSON.stringify({ subscribed: true }));
        this.showSuccessPopup();
      } catch (error) {
        console.error("[WavePlug] Klaviyo subscription error:", error);
        this.showFormError(error.message || "An error occurred. Please try again.");
      } finally {
        this.submitButton.disabled = false;
      }
    });
  }

  calculateNextDisplayDate() {
    const now = new Date();
    return new Date(now.getTime() + this.delayDays * 24 * 60 * 60 * 1000).toISOString();
  }

  checkAndShowPopup() {
    if (this.testMode) return;

    const stored = localStorage.getItem(this.storageKey);
    if (stored) {
      try {
        const { next_display_date } = JSON.parse(stored);
        if (new Date(next_display_date) > new Date()) {
          console.log("[WavePlug popup] Dismissed recently (cooldown until " + next_display_date + ") — scroll trigger suppressed. To retest, run in console: localStorage.removeItem('" + this.storageKey + "')");
          this.showTab();
          return;
        }
      } catch (_) {}
    }

    console.log("[WavePlug popup] Fresh visitor — registering scroll trigger at 30% threshold");
    this.showTab();
    this.autoShowTimer = setTimeout(() => {
      this.showPopup();
    }, this.delaySeconds * 1000);

    this.setupScrollTrigger();
  }

  setupScrollTrigger() {
    try {
      const THRESHOLD = 0.30;
      let fired = false;
      let ready = false;

      setTimeout(() => { ready = true; }, 1000);

      const onScroll = () => {
        try {
          if (!ready || fired) return;
          if (this.isSubscribed()) return;
          if (this.popupModal.classList.contains("popup-modal--active")) return;
          if (this.successModal?.classList.contains("popup-modal--active")) return;

          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable <= 0) {
            console.log("[WavePlug popup] Page not scrollable — trigger cannot fire");
            return;
          }

          const pct = Math.round((window.scrollY / scrollable) * 100);
          // Log every 5% so you can see it counting up
          if (pct % 5 === 0 && pct > 0) {
            console.log("[WavePlug popup] Scroll depth: " + pct + "% (fires at 30%)");
          }

          if (window.scrollY / scrollable >= THRESHOLD) {
            fired = true;
            window.removeEventListener("scroll", onScroll);
            if (this.autoShowTimer) {
              clearTimeout(this.autoShowTimer);
              this.autoShowTimer = null;
            }
            console.log("[WavePlug popup] 30% threshold reached — opening popup via scroll trigger");
            this.showPopup();
          }
        } catch (_) {}
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      console.log("[WavePlug popup] Scroll listener attached — scroll to 30% to trigger");
    } catch (_) {}
  }

  showPopup() {
    // Don't re-open sign-up while success popup is already showing
    if (this.successModal?.classList.contains("popup-modal--active")) return;

    this.popupModal.classList.add("popup-modal--active");
    this.overlay.classList.add("popup-overlay--active");
    this.hideTab();

    if (this.displayTimer) {
      this.startTimer();
    }
  }

  showTab() {
    if (this.tab) this.tab.classList.add("promo-popup__tab--visible");
  }

  hideTab() {
    if (this.tab) this.tab.classList.remove("promo-popup__tab--visible");
  }

  closePopup() {
    this.popupModal.classList.remove("popup-modal--active");
    this.overlay.classList.remove("popup-overlay--active");
    this.showTab();

    if (!this.testMode) {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({ next_display_date: this.calculateNextDisplayDate(), dismissed: false })
      );
    }
  }

  showSuccessPopup() {
    if (this.successModal) {
      // Cancel any pending auto-open so it can't fire over the success popup
      if (this.autoShowTimer) {
        clearTimeout(this.autoShowTimer);
        this.autoShowTimer = null;
      }
      this.closePopup();
      this.hideTab();
      this.successModal.classList.add("popup-modal--active");
      this.successOverlay.classList.add("popup-overlay--active");
      // Success popup stays open until the user explicitly closes it — no timer
    }
  }

  closeSuccessPopup() {
    if (this.successModal) {
      this.successModal.classList.remove("popup-modal--active");
      this.successOverlay.classList.remove("popup-overlay--active");
      this.showTab();
    }
  }

  async handleCopyCode() {
    const discountCode = this.successModal?.querySelector("#DiscountCode--" + this.sectionId);
    if (!discountCode) {
      console.error("[WavePlug] Discount code element not found.");
      return;
    }

    this.copyToClipboardFallback(discountCode.value);

    if (this.copySuccessMsg) {
      this.copySuccessMsg.style.display = "block";
      // Only the "Copied!" confirmation text disappears — the popup stays open
      setTimeout(() => {
        this.copySuccessMsg.style.display = "none";
      }, 3000);
    }
  }

  copyToClipboardFallback(text) {
    function listener(e) {
      e.clipboardData.setData("text/html", text);
      e.clipboardData.setData("text/plain", text);
      e.preventDefault();
    }
    document.addEventListener("copy", listener);
    document.execCommand("copy");
    document.removeEventListener("copy", listener);
  }

  showFormError(message) {
    if (this.formError) {
      this.formError.textContent = message;
      this.formError.style.display = "block";
      setTimeout(() => {
        this.formError.style.display = "none";
      }, 5000);
    }
  }

  startTimer() {
    let timeLeft = this.timerDuration;
    const updateTimer = () => {
      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;
      this.minutesElement.textContent = minutes.toString().padStart(2, "0");
      this.secondsElement.textContent = seconds.toString().padStart(2, "0");
      if (timeLeft > 0) {
        timeLeft--;
        setTimeout(updateTimer, 1000);
      } else {
        this.closePopup();
      }
    };
    updateTimer();
  }
}

document.addEventListener("DOMContentLoaded", function () {
  const sections = document.querySelectorAll('[data-section-type="newsletter-popup"]');
  sections.forEach((section) => {
    new PromoPopup(section.closest("section, .shopify-section"));
  });
});
